import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

const INACTIVITY_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Invalidates all queries when the user returns after inactivity.
 *
 * Service-worker version checking and reload is now handled entirely by
 * `useServiceWorkerUpdate` (mounted via `<AppUpdatePrompt />`). This hook
 * only refreshes data, not the app shell.
 */
export function useAutoRefresh() {
  const qc = useQueryClient();
  const lastActiveRef = useRef(Date.now());

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        lastActiveRef.current = Date.now();
        return;
      }
      const elapsed = Date.now() - lastActiveRef.current;
      if (elapsed > INACTIVITY_THRESHOLD_MS) {
        qc.invalidateQueries();
      }
      lastActiveRef.current = Date.now();
    };

    const handleFocus = () => {
      const elapsed = Date.now() - lastActiveRef.current;
      if (elapsed > INACTIVITY_THRESHOLD_MS) {
        qc.invalidateQueries();
      }
      lastActiveRef.current = Date.now();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [qc]);
}
